import { PropsWithChildren, useContext } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { ActuatorAccessoriesPageData } from 'src/actuators/accessories/ActuatorAccessoriesPageMapping.ts';
import { calculateTotals } from 'src/actuators/checkout/CalculateTotals.ts';
import { Price } from 'src/common/Price/Price';
import { SelectionDataContext } from 'src/projects/components/SelectionDataContext.ts';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { prepareAccessories } from 'src/common/prepareAccessories.ts';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { SkeletonSelection } from 'src/modules/common/skeletons/SkeletonSelection/SkeletonSelection';
import { matchRoutes } from 'react-router-dom';
import { RouteObject } from 'react-router/dist/lib/context';
import { ActuatorRoutes } from 'src/actuators/router.tsx';
import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import { initLocationState, isLocationState } from 'src/actuators/locationStateContext.ts';
import { Counter } from 'src/common/Counter/Counter.tsx';
import { updatedLocationState } from 'src/common/updatedLocationState.ts';
import { ExportTable } from 'shared/projects/ExportTable.ts';
import { removeByLabel, updateQtyByLabel } from 'src/projects/utils/updateQtyByLabel.ts';
import { ProjectSelectionDeviceList } from './ProjectSelectionDeviceList';
import { ProjectSelectionDevice } from './ProjectSelectionDevice';

type FetchActuatorSelectionProps = {
    searchParams: string;
    pathname: string;
    exportData: ExportTable;
    updateSelection: (searchParams: string, exportData: ExportTable) => void;
}

export const FetchActuatorSelection = ({
    searchParams,
    pathname,
    exportData,
    updateSelection,
    children,
} : PropsWithChildren<FetchActuatorSelectionProps>) => {
    const matches = matchRoutes<RouteObject>(ActuatorRoutes, pathname);

    if (matches === null) {
        throw new Error('match failed');
    }

    const [match] = matches;

    const { actuatorLabel } = match.params;

    const locationState = readSearchParam(
        new URLSearchParams(searchParams),
        LOCATION_STORE_PARAM_NAME,
        { ...initLocationState },
        isLocationState,
    );

    if (actuatorLabel !== undefined && actuatorLabel !== locationState.actuatorLabel) {
        locationState.actuatorLabel = actuatorLabel;
    }

    const { userSettings, currency } = useContext(ConfigContext);
    const ctx = useContext(SelectionDataContext);

    const data = (ctx?.data ?? undefined) as ActuatorAccessoriesPageData | undefined;
    if (ctx === null || ctx.loading || data === undefined) {
        return <SkeletonSelection/>;
    }

    const priceCurrency = ctx.currency;
    const { pricePerUnit } = calculateTotals(data.accessories, userSettings, locationState, currency);
    const accessories = prepareAccessories<
    ActuatorAccessoriesPageData['accessories']['accessories']['0']
    >(
        data.accessories.accessories,
        locationState.selectedAccessories,
        userSettings,
        currency,
        (a, b) => sortAccessories(a.accessory, b.accessory),
    );
    return (
        <>
            <ProjectSelectionDeviceList
                title={<I18n messageId={MESSAGES.checkoutActuatorTitle}/>}
            >
                <ProjectSelectionDevice
                    productImage={(
                        <ProductImage
                            imgSrc={data.accessories.actuator.image?.raw.join('') || ''}
                        >
                            <ActuatorIcon/>
                        </ProductImage>
                    )}
                    productLink={(
                        <ViewProductLink
                            userSettings={userSettings}
                            code={data.accessories.actuator.Label}
                            content=""
                        >
                            <span className="vss-device__product-label">
                                {data.accessories.actuator.Label}
                            </span>

                            <div className="vss-device__product-icon">
                                <ArrowRightIcon/>
                            </div>
                        </ViewProductLink>
                    )}
                    productDescription={(
                        <RenderHtml
                            data={data.accessories.actuator.productType?.localized.join('') || ''}
                        />
                    )}
                    quantity={(
                        <Counter
                            size="sm"
                            value={locationState.qty}
                            onChange={(qty) => {
                                updateSelection(
                                    updatedLocationState(
                                        new URLSearchParams(searchParams),
                                        {
                                            ...locationState,
                                            qty,
                                        },
                                    ).toString(),
                                    updateQtyByLabel(
                                        exportData,
                                        locationState.actuatorLabel,
                                        qty,
                                    ),
                                );
                            }}
                        />
                    )}
                    price={(
                        <Price
                            price={pricePerUnit}
                            currency={priceCurrency}
                            locale={userSettings.currentLocale}
                        />
                    )}
                />
            </ProjectSelectionDeviceList>
            {children}
            {accessories.length ? (
                <ProjectSelectionDeviceList
                    title={<I18n messageId={MESSAGES.checkoutAccessoriesTitle}/>}
                >
                    {accessories.map(({ qty, accessory: { item }, price }) => (
                        <ProjectSelectionDevice
                            key={item.Label}
                            productImage={(
                                <ProductImage
                                    imgSrc={item.image?.raw.join('') || ''}
                                >
                                    <ActuatorIcon/>
                                </ProductImage>
                            )}
                            productLink={(
                                <ViewProductLink
                                    userSettings={userSettings}
                                    code={item.Label}
                                    content=""
                                >
                                    {item.Label}

                                    <div className="vss-accessory__product-icon">
                                        <ArrowRightIcon/>
                                    </div>
                                </ViewProductLink>
                            )}
                            productDescription={item.accessoryType?.localized.join('') || ''}
                            quantity={(
                                <Counter
                                    size="sm"
                                    value={qty}
                                    onDelete={() => {
                                        updateSelection(
                                            updatedLocationState(
                                                new URLSearchParams(searchParams),
                                                {
                                                    ...locationState,
                                                    selectedAccessories: locationState
                                                        .selectedAccessories
                                                        .filter((accessory) => accessory
                                                            .label !== item.Label),
                                                },
                                            ).toString(),
                                            removeByLabel(exportData, item.Label),
                                        );
                                    }}
                                    onChange={(quantity) => {
                                        updateSelection(
                                            updatedLocationState(
                                                new URLSearchParams(searchParams),
                                                {
                                                    ...locationState,
                                                    selectedAccessories: locationState
                                                        .selectedAccessories.map(
                                                            (accessory) => {
                                                                if (accessory.label === item.Label) {
                                                                    return {
                                                                        ...accessory,
                                                                        qty: quantity,
                                                                    };
                                                                }
                                                                return accessory;
                                                            },
                                                        ),
                                                },
                                            ).toString(),
                                            updateQtyByLabel(
                                                exportData,
                                                item.Label,
                                                quantity,
                                            ),
                                        );
                                    }}
                                />
                            )}
                            price={(
                                <Price
                                    price={price}
                                    currency={priceCurrency}
                                    locale={userSettings.currentLocale}
                                />
                            )}
                        />
                    ))}
                </ProjectSelectionDeviceList>
            ) : null}
        </>
    );
};
